import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useCreateOwner, type OwnerEntity } from "@/hooks/useOwners";

const phoneRegex = /^\+?[1-9]\d{9,14}$/;

const schema = z.object({
  name: z.string().min(2, "Required").max(100),
  phone: z.string().regex(phoneRegex, "Enter a valid phone (+91…)"),
  email: z.string().email("Invalid email").optional().or(z.literal("")),
  whatsapp: z.string().max(30).optional().or(z.literal("")),
  notes: z.string().max(2000).optional().or(z.literal("")),
});

type FormValues = z.infer<typeof schema>;

interface OwnerCreateDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreated?: (owner: OwnerEntity) => void;
  defaultName?: string;
}

const Field = ({
  label,
  required,
  error,
  children,
}: {
  label: string;
  required?: boolean;
  error?: string;
  children: React.ReactNode;
}) => (
  <div className="space-y-1.5">
    <Label>
      {label} {required && <span className="text-destructive">*</span>}
    </Label>
    {children}
    {error && <p className="text-xs text-destructive">{error}</p>}
  </div>
);

export const OwnerCreateDialog = ({
  open,
  onOpenChange,
  onCreated,
  defaultName,
}: OwnerCreateDialogProps) => {
  const createOwner = useCreateOwner();
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      name: defaultName ?? "",
      phone: "",
      email: "",
      whatsapp: "",
      notes: "",
    },
  });

  const submit = async (values: FormValues) => {
    try {
      const created = await createOwner.mutateAsync({
        name: values.name.trim(),
        phone: values.phone.trim(),
        email: values.email?.trim() || undefined,
        whatsapp: values.whatsapp?.trim() || undefined,
        notes: values.notes?.trim() || undefined,
      });
      toast.success("Owner created");
      onCreated?.(created);
      reset();
      onOpenChange(false);
    } catch (e) {
      toast.error((e as Error).message);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Create owner</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit(submit)} className="space-y-4">
          <Field label="Name" required error={errors.name?.message}>
            <Input
              {...register("name")}
              placeholder="Full legal name"
              autoFocus
            />
          </Field>
          <Field label="Phone" required error={errors.phone?.message}>
            <Input {...register("phone")} placeholder="+919876543210" />
          </Field>
          <Field label="Email" error={errors.email?.message}>
            <Input
              type="email"
              {...register("email")}
              placeholder="owner@example.com"
            />
          </Field>
          <Field label="WhatsApp" error={errors.whatsapp?.message}>
            <Input {...register("whatsapp")} placeholder="+919876543210" />
          </Field>
          <Field label="Notes" error={errors.notes?.message}>
            <Textarea rows={2} {...register("notes")} />
          </Field>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={createOwner.isPending}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={createOwner.isPending}>
              {createOwner.isPending ? "Creating…" : "Create owner"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default OwnerCreateDialog;
